import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { ArrowLeftIcon, ArrowRightIcon } from "../assets/dashboard-icon.svg";
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import { usePensions } from "../hooks/usePensions";
import { PensionRecord } from "../api/pensions";
import { formatAmount } from "../helper/common.helper";
import PensionSidebar from "./pension-sidebar.component";
import { notify } from "../helper/notify";
import "./holiday-pay-calendar.scss";

/* Register the Swedish locale with react-datepicker once at module
   load — `locale="sv"` then swaps month names, weekday headers
   and screen-reader strings to Swedish. */
registerLocale("sv", sv);

/* Portal the calendar popper to <body> so it escapes the section's
   overflow / transform context and is never clipped. Paired with
   `popperProps.strategy: 'fixed'` for viewport-relative positioning. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    ReactDOM.createPortal(children, document.body);

/* Display-only formatter — every rendered date in this section
   shows as yyyy-MM-dd (e.g. 2026-04-15). Kept local instead of
   editing `formatDateForPayout` because that helper is also used
   by other components which still expect the "April 15, 2026"
   long-form output. */
const formatYmd = (value: string | number | Date | null | undefined): string => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
import LoadingCard from '../components/SkeletonLoading'
import { InvoiceTableSkeleton } from "./dashboard/manage-invoice-skeleton.component";
import { EmptyState } from "./dashboard/EmptyState";

export const PensionReportSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t, i18n } = useTranslation();
    /* Re-evaluates whenever i18next switches language; the picker
       re-renders with Swedish month/weekday labels in sv-se and
       falls back to library default (English) otherwise. */
    const calendarLocale = (i18n.language || "").toLowerCase().startsWith("sv") ? "sv" : undefined;
    const isMobile = window.innerWidth <= 768;
    const [showSidebar, setShowSidebar] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [status, setStatus] = useState<string>('');

    // First-mount shimmer skeleton (~1s) when the Pensions tab opens.
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
    useEffect(() => {
        const timer = setTimeout(() => setShowSkeleton(false), 1000);
        return () => clearTimeout(timer);
    }, []);

    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });

    const {
        pensionRecords,
        paginatedPensionRecords,
        totalPages,
        currentPage,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        goToPreviousPage,
        goToNextPage,
        loading,
        error,
        refetch
    } = usePensions({ pageSize: 10, fromDate, toDate });

    useEffect(() => {
        refetch();
    }, [fromDate, toDate, status, currentPage]);

    // Auto-clear error tooltips after 6 seconds
    useEffect(() => {
        if (toolTipError.invalidFromdate || toolTipError.toDateNotSelected || toolTipError.fromDateNotSelected) {
            const timer = setTimeout(() => {
                setToolTipError({
                    invalidFromdate: false,
                    toDateNotSelected: false,
                    fromDateNotSelected: false,
                });
            }, 6000);

            return () => clearTimeout(timer);
        }
    }, [toolTipError]);


    const handleClearFilters = () => {
        try {
            setFromDate(null);
            setToDate(null);
            setStatus('');
            setCurrentPage(1);
            setToolTipError({
                invalidFromdate: false,
                toDateNotSelected: false,
                fromDateNotSelected: false,
            });
        } catch (error) {
            console.error('Error clearing filters:', error);
            notify.error(t('ERROR_CLEARING_FILTERS'));
        }
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (showSkeleton) {
        return (
            <div className={`holiday-pay-layout ${className || ''}`}>
                <div className="holiday-pay-layout-main">
                    <div className="invoice-table-container sn-skeleton-host">
                        <InvoiceTableSkeleton columns={5} filters="full" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`holiday-pay-layout ${className || ''}`}>
            <div className="holiday-pay-layout-main">
                <div className="invoice-table-container">
                    <div className="invoice-table-header">
                        <h3 className="invoice-table-title">{t('PENSION_TRANSACTION_HISTORY')}</h3>
                        <div className="holiday-filter-section">
                            <div className="holiday-filter-inputs">
                                <div className="holiday-filter-input">
                                    <DatePicker
                                        selected={fromDate}
                                        onChange={(d: Date | null) => {
                                            setFromDate(d);
                                            setToolTipError({
                                                invalidFromdate: false,
                                                toDateNotSelected: false,
                                                fromDateNotSelected: false,
                                            });
                                        }}
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText={t('From')}
                                        className="holiday-filter__input"
                                        wrapperClassName="holiday-filter__input-dp"
                                        popperContainer={PopperPortal}
                                        popperProps={{ strategy: 'fixed' }}
                                        popperClassName="holiday-filter__dp-popper"
                                        calendarClassName="holiday-filter__dp-calendar"
                                        onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="scroll"
                                        locale={calendarLocale}
                                        autoComplete="off"
                                        maxDate={new Date()}
                                    />
                                    {toolTipError.fromDateNotSelected && (
                                        <div className="tooltip-error">
                                            {t('PLEASE_SELECT_FROM_DATE')}
                                        </div>
                                    )}
                                    {toolTipError.invalidFromdate && (
                                        <div className="tooltip-error">
                                            {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                        </div>
                                    )}
                                </div>
                                <div className="holiday-filter-input">
                                    <DatePicker
                                        selected={toDate}
                                        onChange={(d: Date | null) => {
                                            setToDate(d);
                                            setToolTipError({
                                                invalidFromdate: false,
                                                toDateNotSelected: false,
                                                fromDateNotSelected: false,
                                            });
                                        }}
                                        dateFormat="yyyy-MM-dd"
                                        placeholderText={t('To')}
                                        className="holiday-filter__input"
                                        wrapperClassName="holiday-filter__input-dp"
                                        popperContainer={PopperPortal}
                                        popperProps={{ strategy: 'fixed' }}
                                        popperClassName="holiday-filter__dp-popper"
                                        calendarClassName="holiday-filter__dp-calendar"
                                        onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="scroll"
                                        locale={calendarLocale}
                                        autoComplete="off"
                                        maxDate={new Date()}
                                        minDate={fromDate || undefined}
                                    />
                                    {toolTipError.toDateNotSelected && (
                                        <div className="tooltip-error">
                                            {t('PLEASE_SELECT_TO_DATE')}
                                        </div>
                                    )}
                                </div>
                                <button className="holiday-filter-clear-btn"
                                    onClick={handleClearFilters}
                                >
                                    {t('CLEAR')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="invoice-table-content">
                        
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID')}</TableColumn>
                                <TableColumn>{t('INVOICE_NUMBER')}</TableColumn>
                                <TableColumn>{t('PENSION_AMOUNT')}</TableColumn>
                                <TableColumn>{t('EMPLOYEE_NAME')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {isMobile ? (
                                // Mobile: Card-based layout
                                paginatedPensionRecords?.map((pension: PensionRecord) => (
                                    <MobileCardWrapper key={pension.invoiceId || Math.random()}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{pension.userId || 'N/A'}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NUMBER')}>
                                            {pension.invoiceNumber ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('PENSION_AMOUNT')}>
                                            {formatAmount(pension.pensionAmount) ?? 'N/A'} {t('kr')}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('EMPLOYEE_NAME')}>
                                            {pension.employeeName ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {pension?.createdDate
                                                ? formatYmd(pension.createdDate)
                                                : 'N/A'}
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                paginatedPensionRecords.map((pension: PensionRecord) => (
                                    <TableRow key={pension.invoiceId || Math.random()}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{pension.userId || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell>{pension.invoiceNumber ?? 'N/A'}</TableCell>
                                        <TableCell>{formatAmount(pension.pensionAmount) ?? 'N/A'} {t('kr')}</TableCell>
                                        <TableCell>{pension.employeeName ?? 'N/A'}</TableCell>
                                        <TableCell>
                                            {pension?.createdDate
                                                ? formatYmd(pension.createdDate)
                                                : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>

                        {/* Loading / empty state — full width, outside the table */}
                        {loading && paginatedPensionRecords.length === 0 && (
                            <LoadingCard />
                        )}
                        {!loading && paginatedPensionRecords.length === 0 && (
                            <EmptyState
                                title={t('NO_PENSIONS_FOUND')}
                                subtitle={error ? String(error) : t('NO_DATA_SUBTITLE')}
                            />
                        )}
                    </div>


                    {/* Pagination */}
                    {totalPages > 1 && paginatedPensionRecords && paginatedPensionRecords.length > 0 && (
                        <div className="invoice-table-pagination">
                            <div className="pagination-controls">
                                <button
                                    className="pagination-button pagination-button--prev"
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    <ArrowLeftIcon />
                                    <span>{t('PREV_STEP')}</span>
                                </button>

                                {getPageNumbers().map((page, index) => {
                                    if (page === '...') {
                                        return (
                                            <button
                                                key={`ellipsis-${index}`}
                                                className="pagination-button pagination-button--number"
                                                disabled
                                            >
                                                ...
                                            </button>
                                        );
                                    }
                                    return (
                                        <button
                                            key={page}
                                            className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                                }`}
                                            onClick={() => setCurrentPage(page as number)}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button
                                    className="pagination-button pagination-button--next"
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    <span>{t('NEXT_STEP')}</span>
                                    <ArrowRightIcon />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}